import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Table,
  Tag,
} from "antd";
import type { DescriptionsProps } from "antd";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { readProjectID } from "../../services/localStorage.Service";
import { useNavigate } from "react-router-dom";
import { GrStatusUnknown } from "react-icons/gr";

import {
  ArrowLeftOutlined,
  CheckOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import EditProjectFormDrawer from "./EditProjectFormDrawer";
import { AppDispatch, RootState } from "../../store/store";
import {
  editProject,
  fetchProjectDetails,
} from "../../store/slices/projectSlice";
import * as S from "./ProjectDetails.styles";
import toast from "react-hot-toast";
import { fetchUsers } from "../../store/slices/userSlice";
import { MdPendingActions } from "react-icons/md";
import { CgQr } from "react-icons/cg";
import Title from "antd/es/skeleton/Title";
import { TranchesTable } from "./components/TranchesTable";
import { TrancheDetails } from "./components/TrancheDetails";
import { AddTrancheDrawer } from "./components/AddTrancheDrawer";

const ValiderButton = styled(Button)`
  background-color: #7ae99b;
  color: #066121;
`;

const optionComposantsColors: Record<string, string> = {
  appartements: "purple",
  villas: "green",
  "locaux commerciaux": "cyan",
  bureaux: "red",
};

const optionUsageColors: Record<string, string> = {
  residentiel: "green",
  professionel: "red",
  commerce: "cyan",
};

// Color mapping for programmation
// const programmationColors: Record<string, string> = {
//   Studios: "purple",
//   F2: "purple",
//   F3: "purple",
//   F4: "purple",
//   F5: "purple",
//   Duplex: "purple",
//   Triplex: "purple",
//   Isolées: "green",
//   Jumelées: "green",
//   Enbande: "green",
//   "centres commerciaux": "cyan",
//   "pied d'immeuble": "cyan",
//   showrooms: "cyan",
//   coworking: "red",
//   "salles de conférences": "red",
//   "bureaux individuels": "red",
//   "open space": "red",
// };

// const orderProgrammation = (programmation: string | null): string[] => {
//   if (!programmation) {
//     return []; // Handle null or empty value
//   }

//   const programmations = programmation.split(",").map((prog) => prog.trim()); // Split and trim

//   // Sort based on the order of keys in programmationColors
//   return programmations.sort((a, b) => {
//     const indexA = Object.keys(programmationColors).indexOf(a);
//     const indexB = Object.keys(programmationColors).indexOf(b);

//     // If an item is not in programmationColors, it gets the lowest priority
//     return (
//       (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
//     );
//   });
// };

// const renderOrderedProgrammationTags = (programmation: string | null) => {
//   const orderedProgrammations = orderProgrammation(programmation);

//   return orderedProgrammations.map((prog) => {
//     const trimmedProg = prog.trim();
//     const color = programmationColors[trimmedProg] || "default"; // Default color fallback
//     return (
//       <Tag key={trimmedProg} color={color} style={{ marginInlineEnd: 4 }}>
//         {trimmedProg.charAt(0).toUpperCase() + trimmedProg.slice(1)}{" "}
//         {/* Capitalize */}
//       </Tag>
//     );
//   });
// };

const StyledDescription = styled(Descriptions)`
  & .ant-descriptions-header .ant-descriptions-title {
    font-size: 16px;
    font-weight: normal;
  }

  & .ant-descriptions-view > table > tbody > tr > th {
    background-color: #e6f2f9;
    font-weight: bold;
  }

  margin-bottom: 20px;
`;

const renderProjectComponents = (composants: string | null) => {
  if (!composants) {
    return <span>Aucune information disponible</span>; // Handle null or empty value
  }
  const types = composants.split(","); // Split the comma-separated string
  return types.map((type) => {
    const trimmedType = type.trim(); // Ensure no leading/trailing spaces
    return (
      <Tag
        key={trimmedType}
        color={optionComposantsColors[trimmedType] || "default"} // Default color fallback
        style={{ marginInlineEnd: 4 }}
      >
        {trimmedType.charAt(0).toUpperCase() + trimmedType.slice(1)}{" "}
        {/* Capitalize */}
      </Tag>
    );
  });
};

const renderUsageComponents = (usage: string | null) => {
  if (!usage) {
    return <span>Aucune information disponible</span>; // Handle null or empty value
  }
  const types = usage.split(","); // Split the comma-separated string
  return types.map((type) => {
    const trimmedType = type.trim(); // Ensure no leading/trailing spaces
    return (
      <Tag
        key={trimmedType}
        color={optionUsageColors[trimmedType] || "default"} // Default color fallback
        style={{ marginInlineEnd: 4 }}
      >
        {trimmedType.charAt(0).toUpperCase() + trimmedType.slice(1)}{" "}
        {/* Capitalize */}
      </Tag>
    );
  });
};

const ProjectDetails: React.FC = () => {
  const { projectDetails, loading } = useSelector(
    (state: RootState) => state.projects
  );
  const { visitesByTranche } = useSelector((state: RootState) => state.visites);
  const [selectedTranche, setSelectedTranche] = useState<number | null>(null);
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isLoading: boolean =
    loading !== "idle" && loading !== "succeeded" && loading !== "failed";

  const showDrawer = () => {
    setOpen(true);
  };
  // State for drawer visibility
    const [addDrawerVisible, setAddDrawerVisible] = useState(false);
  
    // Function to open the edit drawer
    const openAddDrawer = () => {
      setAddDrawerVisible(true);
    };

  const donneesGeneralesDuProjetItems: DescriptionsProps["items"] = [
    { label: "Nom du projet", children: projectDetails.nom_du_projet },
    { label: "Titre foncier", children: projectDetails.titre_foncier },
    {
      label: "Numéro de permis de construire",
      children: projectDetails.numero_de_permis_de_construire,
    },

    {
      label: "Promoteur",
      children: projectDetails.promoteur ? (
        `${projectDetails.promoteur}`
      ) : (
        <GrStatusUnknown />
      ),
    },
    {
      label: "Type de réalisation",
      children: projectDetails.type_realisation,
    },
    {
      label: "Nombre de tranches",
      children:
        projectDetails.type_realisation === "En totalité"
          ? "Aucune"
          : projectDetails.nbr_tranches,
    },
    {
      label: "Date de création",
      children: new Date(projectDetails.created_at).toLocaleDateString(),
    },
    {
      label: "Dernière mise à jour",
      children: new Date(projectDetails.updated_at).toLocaleDateString(),
    },
  ];

  const validateProject = async () => {
    try {
      const id = projectDetails.id;
      const newProject = { is_valide: true };

      // Dispatch actions
      await dispatch(editProject({ newProject, id }))
        .unwrap()
        .then(() => {
          toast.success("Projet validé avec succès");
        })
        .catch((error: any) => {
          toast.error("Une erreur est survenue.");
        });
      await dispatch(fetchProjectDetails(id));
    } catch (error) {
      // Handle and log the error
      console.error("Erreur lors de la validation du projet:", error);

      // Notification on failure
      toast.error("Une erreur est survenue.");
    }
  };

  const handleBack = () => {
    const lastPage = localStorage.getItem("lastPage");

    if (lastPage === "/") {
      navigate("/")
    } else if (lastPage === "/projectsList") {
      navigate("/projectsList")
    } else {
      // Fallback
      navigate("/projectsList")
    }
  };

  useEffect(() => {
    const projectId = readProjectID();
    const fetchProject = async () => {
      if (projectId) {
        await dispatch(fetchProjectDetails(projectId));
        if (projectDetails.etat_projet !== "non visité") {
          //await dispatch(fetchVisitesByProject(projectId));
          await dispatch(fetchUsers());
        }
      }
    };

    fetchProject();
  }, [projectDetails.etat_projet]);

  useEffect(() => {},[projectDetails])


  // At the beginning of your return statement, add a more comprehensive check
if (!projectDetails || !projectDetails.tranches) {
  return <div>Loading project details...</div>;
}

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "10px",
          paddingLeft: "10px",
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={handleBack}
          icon={<ArrowLeftOutlined />}
        >
          Retour
        </Button>
        {/* {projectDetails.etat_projet === "non visité" && (
          <Row justify={"end"} style={{ width: "100%", paddingRight: "10px" }}>
            <Col xs={24} sm={24} md={6} lg={6}>
              <Alert
                message="Statut : Projet non visité"
                type="warning"
                showIcon
              />
            </Col>
          </Row>
        )}
        {(projectDetails.etat_projet === "visité" ||
          projectDetails.etat_projet === "partiellement visité") && (
          <Row justify={"end"} style={{ width: "100%", paddingRight: "10px" }}>
            <Col xs={24} sm={24} md={6} lg={6}>
              <Alert
                message={`Statut : Projet ${projectDetails.etat_projet}`}
                type="info"
                showIcon
              />
            </Col>
          </Row>
        )} */}
        <div>
          {/* {projectDetails.etat_projet === "visité" &&
            !projectDetails.is_valide && (
              <ValiderButton
                icon={<CheckOutlined />}
                variant="solid"
                style={{ marginRight: "10px" }}
                size="small"
                onClick={validateProject}
                loading={isLoading}
              >
                Valider
              </ValiderButton>
            )} */}
        </div>
      </div>
      <Badge.Ribbon
        color={
          projectDetails.etat_projet === "non visité"
            ? "#f50"
            : projectDetails.etat_projet === "à actualiser"
            ? "#e0a333"
            : "#87d068"
        }
        text={"Statut : Projet " + projectDetails.etat_projet}
        style={{ marginRight: "10px" }}
      >
        <Card
          style={{ border: "2px solid #420059", margin: "10px" }}
          title="Caractéristiques générales du projet"
          bordered
        >
          <Row justify={"end"} style={{ width: "100%", paddingRight: "10px" }}>
            <Button
              icon={<EditOutlined />}
              variant="solid"
              color="primary"
              onClick={showDrawer}
            >
              Modifer
            </Button>
          </Row>

          <Descriptions
            layout="vertical"
            size="small"
            column={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 4, xxl: 4 }}
            items={donneesGeneralesDuProjetItems}
            style={{ padding: "0 10px" }}
          />
        </Card>
      </Badge.Ribbon>
      <Card
        style={{ border: "2px solid #420059", margin: "10px" }}
        title="Caractéristiques spécifiques du projet"
        bordered
      >
        <AddTrancheDrawer
        setAddDrawerVisible={setAddDrawerVisible}
        addDrawerVisible={addDrawerVisible}
        projectId={projectDetails.id}
        communeObjectId={projectDetails.tranches && projectDetails.tranches.length > 0 
          ? projectDetails.tranches[0].fk_adresse?.fk_commune_id 
          : null}
      />
      <Row justify={"end"} style={{width:"100%",marginBottom:"10px"}}>
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          onClick={() => setAddDrawerVisible(true)}
        >
          Ajouter une tranche à ce projet
        </Button>
      </Row>
        {/* New Tranches Section */}
        {projectDetails.tranches && projectDetails.tranches.length > 0 && (
          <Card
            style={{ margin: "5px" }}
            styles={{body:{padding:"10px"}}}
            title={
              projectDetails.type_realisation === "Par tranches" &&
              `Tranches du projet (${projectDetails.tranches.length})`
            }
            bordered
          >
            {projectDetails.type_realisation === "Par tranches" && (
              <TranchesTable
                selectedTranche={selectedTranche}
                setSelectedTranche={setSelectedTranche}
                projectDetails={projectDetails}
              />
            )}

            <TrancheDetails
              selectedTranche={selectedTranche}
              projectDetails={projectDetails}
              setSelectedTranche={setSelectedTranche}
            />

            <Row
              justify={"end"}
              style={{ width: "100%", paddingRight: "10px", marginTop: "16px" }}
            >
              {/* <Button
                icon={<EditOutlined />}
                variant="solid"
                color="primary"
                onClick={showDrawer}
              >
                Modifier les tranches
              </Button> */}
            </Row>
          </Card>
        )}
      </Card>
      <EditProjectFormDrawer open={open} setOpen={setOpen} />
    </>
  );
};

export default ProjectDetails;
